import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  BOOKING_DROP_OFF_CATEGORIES,
  BOOKING_DROP_OFF_LABELS,
  BOOKING_DROP_OFF_NOTE_MAX_LENGTH,
  type BookingDropOffCategory,
  type BookingDropOffRow,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { SupportReasonDialog } from "@/components/SupportView";
import {
  fetchBookingDropOffs,
  fetchDropOffReason,
  saveDropOffReason,
} from "@/lib/booking-diagnostics-api";
import { ForbiddenError, UnauthorizedError } from "@/lib/clinician-api";
import {
  rangeEndingAt,
  rangeError,
  stockholmToday,
} from "@/lib/onboarding-funnel";
import { adminPatientHref } from "@/lib/patient-links";
import { fetchSupportAccessStatus } from "@/lib/support-access-api";
import { SUPPORT_REASON_REQUIRED } from "@/lib/support-view";

// TES-1102 S3 (Emelie 2026-10-04) — Bokningsdiagnostik: the people who
// started booking their first care meeting in the period and did not book.
//
// Internal and admin only. Unlike the onboarding funnel it names people, so
// the API logs the list as a read of each of them, and recording a reason
// asks for today's support access reason first. "Kontaktbar" (TES-1106) is
// "may be contacted for product feedback": the product-feedback consent is
// granted and the e-mail is verified. App use and the support opt-out stay
// as their own columns, so the list says how someone can be reached and why
// someone is not contactable. The private note is only ever shown in the
// form, never in the list or the funnel.

const PRESETS = [
  { days: 7, label: "7 dagar" },
  { days: 30, label: "30 dagar" },
  { days: 90, label: "90 dagar" },
] as const;

const REACHED_LABEL: Record<BookingDropOffRow["reached"], string> = {
  payment_step: "Betalsteget",
  booking_intent: "”Boka vårdmöte”",
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
    timeZone: "Europe/Stockholm",
  });
}

function YesNo({ value, good = true }: { value: boolean; good?: boolean }) {
  const ok = value === good;
  return (
    <span className={ok ? "text-textPrimary" : "text-textSecondary"}>
      {value ? "Ja" : "Nej"}
    </span>
  );
}

export function BookingDiagnosticsRoute() {
  const { signOut, claims } = useAuth();
  const signOutRef = useRef(signOut);
  signOutRef.current = signOut;
  const [range, setRange] = useState(() => rangeEndingAt(stockholmToday(), 30));
  const [rows, setRows] = useState<BookingDropOffRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  // TES-1106 — "får kontaktas för produktfeedback" only.
  const [onlyContactable, setOnlyContactable] = useState(false);
  const shown = rows?.filter((r) => !onlyContactable || r.contactable) ?? null;
  const invalid = rangeError(range.from, range.to);

  const load = useCallback(async () => {
    if (rangeError(range.from, range.to)) return;
    setError(null);
    try {
      setRows((await fetchBookingDropOffs(range.from, range.to)).rows);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOutRef.current();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [range]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-start justify-between gap-md flex-wrap">
        <div className="flex flex-col gap-xs">
          <h1 className="font-display text-3xl tracking-tight">
            Bokningsdiagnostik
          </h1>
          <p className="text-sm text-textSecondary max-w-2xl">
            Personer som påbörjade bokningen av sitt första vårdmöte under
            perioden men inte bokade. Internt: listan loggas som åtkomst till
            varje person. Kontaktbar = får kontaktas för produktfeedback:
            patienten har samtyckt till det och har en verifierad e-post.
          </p>
        </div>
        <div className="flex flex-col gap-sm items-end">
          <div className="flex gap-xs" role="group" aria-label="Snabbval">
            {PRESETS.map((p) => (
              <button
                key={p.days}
                type="button"
                onClick={() =>
                  setRange(rangeEndingAt(stockholmToday(), p.days))
                }
                className="px-md py-sm rounded-md border border-surfaceMuted text-sm"
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="flex gap-sm items-end">
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Från
              <input
                type="date"
                value={range.from}
                max={range.to}
                onChange={(e) =>
                  setRange((r) => ({ ...r, from: e.target.value }))
                }
                className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
              />
            </label>
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Till
              <input
                type="date"
                value={range.to}
                min={range.from}
                onChange={(e) =>
                  setRange((r) => ({ ...r, to: e.target.value }))
                }
                className="px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary"
              />
            </label>
          </div>
          {invalid ? (
            <p className="text-xs text-danger" role="alert">
              {invalid}
            </p>
          ) : null}
        </div>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <label className="flex items-center gap-sm text-sm">
          <input
            type="checkbox"
            checked={onlyContactable}
            onChange={(e) => setOnlyContactable(e.target.checked)}
          />
          Visa bara de som får kontaktas för produktfeedback
        </label>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
          Ingen påbörjade en bokning utan att boka under perioden.
        </div>
      ) : null}

      {shown && rows && rows.length > 0 && shown.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary text-sm">
          Ingen under perioden får kontaktas för produktfeedback.
        </div>
      ) : null}

      {shown && shown.length > 0 ? (
        <div className="border border-surfaceMuted rounded-md bg-surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                <th className="px-md py-sm font-medium">Namn</th>
                <th className="px-md py-sm font-medium">Nådde</th>
                <th className="px-md py-sm font-medium">E-post verifierad</th>
                <th className="px-md py-sm font-medium">Appen</th>
                <th className="px-md py-sm font-medium">Avregistrerad</th>
                <th className="px-md py-sm font-medium">
                  Samtycke produktfeedback
                </th>
                <th className="px-md py-sm font-medium">Kontaktbar</th>
                <th className="px-md py-sm font-medium">Orsak</th>
                <th className="px-md py-sm font-medium" />
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <DropOffRow
                  key={r.patientId}
                  row={r}
                  actingRole={claims?.role}
                  open={open === r.patientId}
                  onToggle={() =>
                    setOpen((o) => (o === r.patientId ? null : r.patientId))
                  }
                  onSaved={() => {
                    setOpen(null);
                    void load();
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function DropOffRow({
  row,
  actingRole,
  open,
  onToggle,
  onSaved,
}: {
  row: BookingDropOffRow;
  actingRole: string | undefined;
  open: boolean;
  onToggle: () => void;
  onSaved: () => void;
}) {
  return (
    <>
      <tr className="border-t border-surfaceMuted align-top">
        <td className="px-md py-sm">
          <span className="font-medium">{row.patientName}</span>
        </td>
        <td className="px-md py-sm">
          {REACHED_LABEL[row.reached]}
          <span className="block text-xs text-textSecondary">
            {fmtDate(row.reachedAt)}
          </span>
        </td>
        <td className="px-md py-sm">
          <YesNo value={row.emailVerified} />
        </td>
        <td className="px-md py-sm">
          <YesNo value={row.reachableInApp} />
        </td>
        <td className="px-md py-sm">
          <YesNo value={row.optedOut} good={false} />
        </td>
        <td className="px-md py-sm">
          <YesNo value={row.feedbackConsent} />
        </td>
        <td className="px-md py-sm">
          <span
            className={
              row.contactable
                ? "rounded-pill bg-brand/10 text-brand px-sm py-[2px] text-xs"
                : "rounded-pill bg-surfaceMuted text-textSecondary px-sm py-[2px] text-xs"
            }
          >
            {row.contactable ? "Kontaktbar" : "Ej kontaktbar"}
          </span>
        </td>
        <td className="px-md py-sm">
          {row.reason ? (
            <>
              {BOOKING_DROP_OFF_LABELS[row.reason.category]}
              {row.reason.hasNote ? (
                <span className="block text-xs text-textSecondary">
                  Anteckning finns
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-textSecondary">–</span>
          )}
        </td>
        <td className="px-md py-sm whitespace-nowrap">
          <div className="flex flex-col gap-xs items-start">
            <Link
              to={adminPatientHref(actingRole, row.patientId)}
              className="text-brand hover:underline"
            >
              Patienten
            </Link>
            <Link
              to={`/support-inbox/${encodeURIComponent(row.patientId)}`}
              className="text-brand hover:underline"
            >
              {row.supportThreadStarted
                ? "Supporttråden"
                : "Skriv i supporttråden"}
            </Link>
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              className="text-brand hover:underline"
            >
              {row.reason ? "Ändra orsak" : "Registrera orsak"}
            </button>
          </div>
        </td>
      </tr>
      {open ? (
        <tr className="bg-background">
          <td colSpan={9} className="px-md py-md">
            <ReasonForm patientId={row.patientId} onSaved={onSaved} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

type FormState = "checking" | "reason" | "loading" | "ready" | "cancelled";

function ReasonForm({
  patientId,
  onSaved,
}: {
  patientId: string;
  onSaved: () => void;
}) {
  const { signOut } = useAuth();
  const [state, setState] = useState<FormState>("checking");
  const [category, setCategory] = useState<BookingDropOffCategory | "">("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      if (e instanceof ForbiddenError && e.code === SUPPORT_REASON_REQUIRED) {
        setState("reason");
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  useEffect(() => {
    let live = true;
    fetchSupportAccessStatus(patientId)
      .then((s) => {
        if (live) setState(s.active ? "loading" : "reason");
      })
      .catch((e: unknown) => {
        if (live) fail(e);
      });
    return () => {
      live = false;
    };
  }, [patientId, fail]);

  useEffect(() => {
    if (state !== "loading") return;
    let live = true;
    fetchDropOffReason(patientId)
      .then(({ reason }) => {
        if (!live) return;
        setCategory(reason?.category ?? "");
        setNote(reason?.note ?? "");
        setState("ready");
      })
      .catch((e: unknown) => {
        if (live) fail(e);
      });
    return () => {
      live = false;
    };
  }, [state, patientId, fail]);

  async function save() {
    if (!category || busy) return;
    setBusy(true);
    setError(null);
    try {
      await saveDropOffReason(patientId, {
        category,
        note: note.trim() ? note.trim() : null,
      });
      onSaved();
    } catch (e) {
      fail(e);
      setBusy(false);
    }
  }

  if (state === "reason") {
    return (
      <SupportReasonDialog
        patientId={patientId}
        onGranted={() => setState("loading")}
        onCancel={() => setState("cancelled")}
      />
    );
  }
  if (state === "cancelled") {
    return (
      <p className="text-sm text-textSecondary">
        En orsak för åtkomst krävs för att registrera varför hon inte bokade.
      </p>
    );
  }
  if (state !== "ready") {
    return <p className="text-sm text-textSecondary">Hämtar…</p>;
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      className="flex flex-col gap-sm max-w-xl"
    >
      <label className="flex flex-col gap-xs text-sm">
        Varför bokade hon inte?
        <select
          value={category}
          onChange={(e) =>
            setCategory(e.target.value as BookingDropOffCategory | "")
          }
          className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm"
        >
          <option value="">Välj kategori</option>
          {BOOKING_DROP_OFF_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {BOOKING_DROP_OFF_LABELS[c]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-xs text-sm">
        Privat anteckning (valfri)
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={BOOKING_DROP_OFF_NOTE_MAX_LENGTH}
          rows={3}
          className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm"
        />
        <span className="text-xs text-textSecondary">
          Sparas krypterat och visas aldrig i statistiken.
        </span>
      </label>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <button
          type="submit"
          disabled={!category || busy}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara orsak"}
        </button>
      </div>
    </form>
  );
}
